var DateTime = luxon.DateTime;

var DeliveryDatePicker = class extends HTMLDivElement {
  dateFormat = 'yyyy-MM-dd';
  timeZone = 'America/New_York';

  cart = null;

  checkoutButton = null;
  checkoutButtonText = null;
  checkoutButtonTotalPrice = null;

  selectDeliveryMethodContainer = null;
  selectDeliveryDatePickerContainer = null;
  selectDeliveryDatePickerLoaderContainer = null;
  selectDeliveryDatePickerTitle = null;
  selectDeliveryDatePickerError = null;

  lowestCostPrice = null;

  deliveryMethods = null;
  deliveryDatePickerZipInput = null;
  deliveryDatePickerCommercialInput = null;

  selectedDeliveryDate = null;
  shippingRates = {};

  zipCodeChangeDelay = 500;
  zipCodeChangeTimeout = null;

  // The drawer re-renders (and re-inits this picker) after a cart change, so
  // just keep the cached cart current for totals in the meantime.
  _onCartUpdated = (event) => {
    if (event.detail?.cart) this.cart = event.detail.cart;
  };

  connectedCallback() {
    this.checkoutButton = this.querySelector('[form="mini-cart-form"]');

    this.checkoutButton.disabled = true;

    this.checkoutButtonText = this.checkoutButton.querySelector('.checkout-button__text');
    this.checkoutButtonTotalPrice = this.checkoutButton.querySelector('.checkout-button__total-price');
    this.checkoutButtonText.textContent = 'Enter Zip Code';

    this.selectDeliveryMethodContainer = this.querySelector('.select-delivery-method');
    this.selectDeliveryDatePickerContainer = this.querySelector('.select-delivery-date-picker');
    this.selectDeliveryDatePickerLoaderContainer = this.querySelector('.select-delivery-date-picker-loader');
    this.selectDeliveryDatePickerTitle = this.querySelector('.select-delivery-date-picker-title');
    this.selectDeliveryDatePickerError = this.querySelector('.select-delivery-date-picker-error');

    this.lowestCostPrice = this.querySelector('label .lowest-cost');

    this.deliveryMethods = this.querySelectorAll('.delivery-method');
    this.deliveryDatePickerZipInput = this.querySelector('#delivery-date-picker-zip');
    this.deliveryDatePickerCommercialInput = this.querySelector('#delivery-date-picker-commercial');

    [this.selectDeliveryMethodContainer
      , this.selectDeliveryDatePickerContainer]
      .forEach(container => container.classList.add('hidden'));

    this.deliveryMethods.forEach(method => method.addEventListener('change', this._onDeliveryMethodChange.bind(this)));
    this.deliveryDatePickerZipInput.addEventListener('input', this._onZipCodeChange.bind(this));
    this.deliveryDatePickerCommercialInput.addEventListener('change', this._onCommercialChange.bind(this));

    const flatpickrOptions = {
      dateFormat: this.dateFormat,
      enable: [],
      minDate: new Date(),
      maxDate: new Date(),
      inline: true,
      locale: {
        weekdays: {
          shorthand: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
          longhand: [
            "Sunday",
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ],
        },
      },
      formatDate: this._formatDate.bind(this),
      parseDate: this._parseDate.bind(this),
      onChange: this._onChange.bind(this),
      onDayCreate: this._onDayCreate.bind(this)
    };

    this.picker = flatpickr("#delivery-date-picker", flatpickrOptions);

    document.addEventListener('cart:updated', this._onCartUpdated);

    this._init();
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this._onCartUpdated);
  }

  _sum(values) {
    return values.reduce((working, next) => working + next, 0);
  }

  _onZipCodeChange(event) {
    event.preventDefault();
    event.stopPropagation();

    this.checkoutButton.disabled = true;

    const value = this._formatZipCode(event.target.value);
    const isValid = this._isValidZipCode(value);

    this.selectDeliveryMethodContainer.classList.add('hidden');
    this.selectDeliveryDatePickerError.classList.add('hidden');
    this.selectDeliveryDatePickerContainer.classList.add('hidden');
    this.deliveryMethods.forEach(method => {
      method.checked = false;
    });

    this.deliveryMethods.forEach(method => method.parentNode.classList.remove('active'));

    this.selectDeliveryDatePickerTitle.classList.add('hidden');

    this.checkoutButtonText.textContent = 'Enter Zip Code';

    event.target.value = value;

    // A five digit zip code is valid on its own, but the shopper may still be
    // typing the +4 extension, so wait for a pause before hitting the cart.
    clearTimeout(this.zipCodeChangeTimeout);

    if (isValid) {
      this.zipCodeChangeTimeout = setTimeout(this._submitZipCode.bind(this, value), this.zipCodeChangeDelay);
    }
  }

  async _submitZipCode(value) {
    // Nothing more can be typed once the +4 extension is complete.
    if (value.length === 10) {
      this.deliveryDatePickerZipInput.blur();
    }

    this._setIsLoading(true);

    await this._changeCart({ _deliveryZipCode: value });

    this.selectDeliveryMethodContainer.classList.remove('hidden');

    this.lowestCostPrice.textContent = '';

    this.checkoutButtonText.textContent = 'Select Shipping';

    this._setIsLoading(false);

    const hasSelectedMethod = Array.from(this.deliveryMethods).some(method => method.checked);

    if (!hasSelectedMethod && this.deliveryMethods.length > 0) {
      this.deliveryMethods[0].click();
    }
  }

  async _onCommercialChange(event) {
    const residential = event.target.checked ? 'false' : 'true';

    this._setIsLoading(true);

    await this._changeCart({ _residential: residential });

    const selectedMethod = Array.from(this.deliveryMethods).find(method => method.checked);

    if (selectedMethod) {
      await this._handleDeliveryMethodSet(selectedMethod.value);
    }

    this._setIsLoading(false);
  }

  async _fetchShippingRates() {
    let response = null;

    try {
      // v2: each rate carries its per-package breakdown, and its price is the
      // sum of the packages - a candy overflow box quoted on ground is no
      // longer charged at the ice cream's rate.
      response = await fetch('/apps/graeters-admin-console/api/v2/shipping-options', {
        body: JSON.stringify({ cart: this.cart }),
        headers: {
          'Content-Type': 'application/json',
          'x-shopify-shop-domain': Shopify.shop
        },
        method: 'POST',
      });
    } catch (error) {
      console.error(error);
    }

    return response && response.ok ? await response.json() : null;
  }

  async _handleDeliveryMethodSet(value) {
    const response = await this._fetchShippingRates();

    if (response) {
      const { defaultDate, maxDate, minDate, shippingRates } = response;

      const shippingRateList = Object.keys(shippingRates);

      this.shippingRates = shippingRates;

      this.picker.set('minDate', minDate);
      this.picker.set('maxDate', maxDate);
     
      if (shippingRateList.length > 0) {
        this.selectedDeliveryDate = shippingRates[defaultDate] ? defaultDate : shippingRateList[0];
        
        this.picker.set('enable', shippingRateList);
      }

      if (this.selectedDeliveryDate) {
        this.picker.jumpToDate(this.selectedDeliveryDate, true);
        this.picker.setDate(this.selectedDeliveryDate, true, this.dateFormat);
      }
    }

    this.selectDeliveryDatePickerError.classList.add('hidden');

    if (value === 'scheduled') {
      if (this.selectedDeliveryDate) {
        this.selectDeliveryDatePickerContainer.classList.remove('hidden');
      }
    } else {
      const [fixedRate] = Object.entries(this.shippingRates)
        .map(([date, cost]) => ({ date, price: cost.price }));

      this.selectedDeliveryDate = fixedRate ? fixedRate.date : null;

      
      
      const priceWithShipping = (fixedRate ? this.cart.total_price + (fixedRate.price * 100) : this.cart.total_price) / 100;

      this.checkoutButtonTotalPrice.textContent = `${this._formatMoney(priceWithShipping)} USD`;
      this.lowestCostPrice.textContent = fixedRate ? `(${this._formatMoney(fixedRate.price)})` : '';

      this.selectDeliveryDatePickerContainer.classList.add('hidden');
    }

    const hasSelectedDate = !!this.selectedDeliveryDate;

    if (this.selectedDeliveryDate) {
      const shippingRate = this.shippingRates[this.selectedDeliveryDate];

      if (shippingRate) {
        await this._changeCart(this._deliveryPropertiesFor(shippingRate, this.selectedDeliveryDate));
      }

      const deliveryDateTitle = this.selectDeliveryDatePickerTitle;
      const deliveryDate = deliveryDateTitle.querySelector('.delivery-date');
      const separator = deliveryDateTitle.querySelector('.separator');

      [deliveryDateTitle, deliveryDate, separator].forEach(element => element.classList.remove('hidden'));

      deliveryDate.textContent = DateTime.fromFormat(this.selectedDeliveryDate, this.dateFormat, { zone: this.timeZone }).toLocaleString(DateTime.DATE_SHORT);
    } else {
      this.selectDeliveryDatePickerError.classList.remove('hidden');

      const errorMessage = this.selectDeliveryDatePickerError.querySelector('p');

      errorMessage.innerHTML = `We do not support shipping to <strong>${this.deliveryDatePickerZipInput.value}</strong>. Please choose another zip code.`;
    }

    this.checkoutButton.disabled = !hasSelectedDate;
    this.checkoutButtonText.textContent = hasSelectedDate ? 'Checkout' : 'Select Date';
  }

  async _onDeliveryMethodChange(event) {
    event.preventDefault();
    event.stopPropagation();

    this.lowestCostPrice.textContent = '';

    this.deliveryMethods.forEach(method => method.parentNode.classList.remove('active'));

    event.target.parentNode.classList.add('active');

    this._setIsLoading(true);

    const value = event.target.value;

    await this._changeCart({ _deliveryOption: value });

    await this._handleDeliveryMethodSet(value);

    this._setIsLoading(false);
  }

  async _fetchCart() {
    let response = null;

    try {
      response = await fetch(`${
        window.themeVariables.routes.cartUrl
      }.js`, {
        method: "GET",
        cache: "no-store",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      });
    } catch (error) {
      console.error(error);
    }

    return response && response.ok ? await response.json() : null;
  }

  async _init() {
    this.cart = await this._fetchCart();

    const items = this.cart?.items || [];

    const { deliveryZipCode, deliveryOption, deliveryServicePrice, estimatedDeliveryDate, residential } = items.reduce((working, next) => {
      working.deliveryZipCode = working.deliveryZipCode || next.properties._deliveryZipCode;
      working.deliveryOption = working.deliveryOption || next.properties._deliveryOption;
      working.deliveryServicePrice = working.deliveryServicePrice || next.properties._deliveryServicePrice;
      working.estimatedDeliveryDate = working.estimatedDeliveryDate || next.properties._estimatedDeliveryDate;
      working.residential = working.residential || next.properties._residential;

      return working;
    }, {});

    if (deliveryZipCode) {
      this.deliveryDatePickerZipInput.value = this._formatZipCode(deliveryZipCode);

      this.selectDeliveryMethodContainer.classList.remove('hidden');
    }

    if (deliveryZipCode && deliveryOption) {
      this.deliveryMethods.forEach(method => {
        if (method.value === deliveryOption) {
          method.checked = true;
          method.parentNode.classList.add('active');
        }
      });

      await this._handleDeliveryMethodSet(deliveryOption);
    }

    if (deliveryZipCode && deliveryOption && deliveryOption === 'scheduled' && estimatedDeliveryDate) {
      this.selectedDeliveryDate = estimatedDeliveryDate;

      this.picker.jumpToDate(estimatedDeliveryDate, true);
      this.picker.setDate(estimatedDeliveryDate, true, this.dateFormat);

      this.selectDeliveryDatePickerContainer.classList.remove('hidden');

      this.checkoutButton.disabled = false;
      this.checkoutButtonText.textContent = 'Checkout';

      const deliveryDateTitle = this.selectDeliveryDatePickerTitle;
      const deliveryDate = deliveryDateTitle.querySelector('.delivery-date');
      const separator = deliveryDateTitle.querySelector('.separator');

      [deliveryDateTitle, deliveryDate, separator].forEach(element => element.classList.remove('hidden'));

      deliveryDate.textContent = DateTime.fromFormat(estimatedDeliveryDate, this.dateFormat, { zone: this.timeZone }).toLocaleString(DateTime.DATE_SHORT);
    }

    // Each line holds only its own package's price, so the total comes from
    // the selected rate when it is loaded. Flexible lines each carry their own
    // package's date, so that rate is the one the method set resolved to.
    const selectedRateDate = deliveryOption === 'scheduled' ? estimatedDeliveryDate : this.selectedDeliveryDate;
    const selectedRate = selectedRateDate && this.shippingRates[selectedRateDate];

    if (selectedRate || deliveryServicePrice) {
      const shippingPrice = selectedRate ? selectedRate.price * 100 : Number(deliveryServicePrice);
      const servicePrice = (this.cart.total_price + shippingPrice) / 100;

      this.checkoutButtonTotalPrice.textContent = `${this._formatMoney(servicePrice)} USD`;
    }

    if (this.deliveryDatePickerCommercialInput.disabled) {
      if (items.length > 0) {
        await this._changeCart({ _residential: `${residential === '' || residential === 'true'}` });
      }
    } else {
      this.deliveryDatePickerCommercialInput.checked = residential === "false";
    }

    this._setIsLoading(false);
  }

  _formatDate(date, _) {
    return DateTime.fromJSDate(date).setZone(this.timeZone, { keepLocalTime: true }).toISODate();
  }

  _parseDate(dateString, format) {
    return DateTime.fromFormat(dateString, format, { zone: this.timeZone }).toJSDate();
  }

  /**
   * Each line takes the service and price of the package it ships in, so a
   * candy or merch box rerated onto ground carries the ground code while the
   * ice cream beside it flies. A line split across packages on different
   * services stays with the rate's own service, and one the rate does not
   * list falls back to its package id, then to the rate itself.
   */
  _deliveryPropertiesFor(shippingRate, estimatedDeliveryDate) {
    const packages = Array.isArray(shippingRate.packages) ? shippingRate.packages : [];
    const codesByPackageId = shippingRate.serviceCodesByPackageId || {};

    return (item) => {
      const containing = packages.filter(pkg =>
        (pkg.items || []).some(({ variantId }) => variantId === item.variant_id));

      const sameService = containing.length > 0 && containing.every(({ code }) => code === containing[0].code);

      const packageCode = codesByPackageId[item.properties?._packageId];

      const pkg = sameService
        ? containing[0]
        : containing.find(({ code }) => code === shippingRate.code)
          || (containing.length === 0 && packageCode && packages.find(({ code }) => code === packageCode))
          || null;

      const code = pkg ? pkg.code : shippingRate.code;
      const price = pkg ? pkg.price : shippingRate.prices[0];

      // Flexible lands each package on its own date; scheduled packages all
      // carry the selected one.
      return {
        _deliveryServiceCode: code,
        _deliveryServicePrice: (price * 100).toFixed(),
        _estimatedDeliveryDate: (pkg && pkg.deliveryDate) || estimatedDeliveryDate
      };
    };
  }

  // Queued with the drawer's quantity changes so the cart read below always
  // sees the latest quantities and our property writes can't overwrite them.
  _changeCart(properties) {
    return window.enqueueCartRequest(() => this._applyCartProperties(properties));
  }

  // `properties` is either one set for every line or a function returning the
  // set for a given line.
  async _applyCartProperties(properties) {
    let cart = await this._fetchCart();

    const propertiesFor = (item) => {
      const next = { ...(typeof properties === 'function' ? properties(item) : properties) };

      const isGiftCard = item.properties?._packageId === 'GIFTCARD'
        || item.handle === 'physical-gifts'
        || item.product_title === 'Physical Gifts'
        || item.properties?.Attribute?.targeted_product === 'Physical';

      if (isGiftCard) {
        next._deliveryServiceCode = 'gift_card';
        next._deliveryServicePrice = '0';
        next._estimatedDeliveryDate = DateTime.now({ zone: 'America/New_York' }).plus({ days: 14 }).toISODate();
      }

      return next;
    };

    // Updating a line's properties changes its key and can merge lines, so
    // always pull the next item from the freshest cart instead of a stale list.
    const needsUpdate = (item) =>
      Object.entries(propertiesFor(item)).some(([name, value]) => (item.properties || {})[name] !== value);

    const attempted = new Set();

    while (cart) {
      this.cart = cart;

      const item = cart.items.find(needsUpdate);

      if (!item || attempted.has(item.key)) break;

      attempted.add(item.key);

      let response = null;

      try {
        const updatedProperties = { ...item.properties, ...propertiesFor(item) };

        response = await fetch(`${
          window.themeVariables.routes.cartUrl
        }/change.js`, {
          // change.js resets the line to quantity 1 when quantity is omitted,
          // so carry the current quantity through with the property update.
          body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: updatedProperties }),
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          }
        });
      } catch (error) {
        console.error(error)
      }

      if (!response || !response.ok) break;

      cart = await response.json();
    }
  }

  async _onChange(_, estimatedDeliveryDate, __) {
    if (!!estimatedDeliveryDate && this.selectedDeliveryDate !== estimatedDeliveryDate) {
      this._setIsLoading(true);

      this.checkoutButton.disabled = true;

      this.selectedDeliveryDate = estimatedDeliveryDate;

      const deliveryDateTitle = this.selectDeliveryDatePickerTitle;
      const deliveryDate = deliveryDateTitle.querySelector('.delivery-date');
      const separator = deliveryDateTitle.querySelector('.separator');

      [deliveryDateTitle, deliveryDate, separator].forEach(element => element.classList.remove('hidden'));

      const shippingRate = this.shippingRates[estimatedDeliveryDate];

      deliveryDate.textContent = DateTime.fromFormat(estimatedDeliveryDate, this.dateFormat, { zone: this.timeZone }).toLocaleString(DateTime.DATE_SHORT);

      if (shippingRate) {
        const shippingRatePrice = shippingRate.price * 100;
        const shippingPriceTotal = this.cart.total_price + shippingRatePrice;
        const shippingPrice = shippingPriceTotal / 100;

        this.checkoutButtonTotalPrice.textContent = `${this._formatMoney(shippingPrice)} USD`;

        await this._changeCart(this._deliveryPropertiesFor(shippingRate, estimatedDeliveryDate));
      }

      this.checkoutButton.disabled = false;

      this._setIsLoading(false);
    }
  }

  _setIsLoading(isLoading) {
    if (isLoading) {
      this.selectDeliveryDatePickerLoaderContainer.classList.remove('hidden');
    } else {
      this.selectDeliveryDatePickerLoaderContainer.classList.add('hidden');
    }
  }

  _onDayCreate(_, __, fp, dayElem) {
    const dateStr = fp.formatDate(dayElem.dateObj, this.dateFormat);

    const isDisabled = dayElem.classList.contains('flatpickr-disabled');

    const shippingRate = this.shippingRates[dateStr];

    if (shippingRate && !isDisabled) { // Add a span with the price for the date
      const priceElem = document.createElement("div");
      priceElem.className = "flatpickr-day-cell-rate";
      priceElem.textContent = this._formatMoney(shippingRate.price);
      dayElem.appendChild(priceElem);
    } else if (!isDisabled) { // Add a span with a "-" if there is no price for the date
      const priceElem = document.createElement("div");
      priceElem.className = "flatpickr-day-cell-rate";
      priceElem.textContent = "-";
      dayElem.appendChild(priceElem);
    }
  }
  
  // Normalizes any input into either "12345" or the hyphenated ZIP+4 "12345-6789".
  _formatZipCode(value) {
    const digits = value.trim().replace(/[^0-9]/g, '').slice(0, 9);

    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  _isValidZipCode(value) {
    return /^\d{5}(-\d{4})?$/.test(value);
  }

  _formatMoney(amount) {
    return `$${amount.toFixed(2).replace(/\.00$/, '')}`;
  }
};

window.customElements.define("delivery-date-picker", DeliveryDatePicker, { extends: "div" });
